import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { ChatBotButton } from '@web/app/box-features/chatbots';
import { EditorRoutes } from '@web/app/box-features/editor';
import { BoxHomeRoutes } from '@web/app/box-features/home';
import { AccessKeysRoutes } from '@web/app/features/access-keys';
import { AgentCatalogRoutes, MyAgentsRoutes } from '@web/app/features/agents';
import { CatalogViewRoutes } from '@web/app/features/catalog-view';
import { CompareChatsRoutes } from '@web/app/features/compare-chats';
import { LogsRoutes } from '@web/app/features/logs';
import { MarketplaceRoutes } from '@web/app/features/marketplace';
import { MyPageRoutes } from '@web/app/features/my-page';
import { OpenApiKeysRoutes } from '@web/app/features/openapi-keys';
import { PolicyRoutes } from '@web/app/features/policy';
import { PromptsRoutes } from '@web/app/features/prompts';
import { StyleRoutes } from '@web/app/features/styling';

import { isProdEnvironment } from '@codes/web-core';

import { AuthGuard } from './guards';
import { AuthRoutes } from '../features/auth';
import { CatalogRoutes } from '../features/catalogs';
import { NewHomeRoutes } from '../features/home';
import { TeamRoutes } from '../features/teams';
import { WorkspaceContextRedirect } from '../shared/components/WorkspaceContextRedirect';

const BoxLayout = () => {
    const isProd = isProdEnvironment();

    return (
        <>
            <Outlet />
            {!isProd && <ChatBotButton />}
        </>
    );
};

const ProtectedLayout = () => {
    return (
        <AuthGuard>
            <Outlet />
        </AuthGuard>
    );
};

const router = createBrowserRouter([
    {
        path: '/',
        element: <WorkspaceContextRedirect />,
    },
    {
        path: '/box',
        element: <ProtectedLayout />,
        children: [
            {
                element: <BoxLayout />,
                children: [
                    { index: true, element: <Navigate to="/box/home" replace /> },
                    { path: 'home', element: <BoxHomeRoutes /> },
                    { path: '*', element: <EditorRoutes /> },
                ],
            },
        ],
    },
    {
        path: '/',
        element: <ProtectedLayout />,
        children: [
            // Workspace routes
            { path: '/w', element: <NewHomeRoutes /> },
            { path: '/w/*', element: <NewHomeRoutes /> },

            // Workspace-level routes (not project dependent)
            { path: `/w/:workspaceId/teams/*`, element: <TeamRoutes /> },
            { path: `/w/:workspaceId/my-page/*`, element: <MyPageRoutes /> },
            { path: `/w/:workspaceId/openapi-keys/*`, element: <OpenApiKeysRoutes /> },
            { path: `/w/:workspaceId`, element: <NewHomeRoutes /> },

            // Project-level routes
            { path: `/w/:workspaceId/p/:projectId`, element: <NewHomeRoutes /> },
            { path: `/w/:workspaceId/p/:projectId/catalogs/*`, element: <CatalogRoutes /> },
            { path: `/w/:workspaceId/p/:projectId/catalog-view/*`, element: <CatalogViewRoutes /> },

            // AI routes (project-level)
            { path: `/w/:workspaceId/p/:projectId/compare-chats/*`, element: <CompareChatsRoutes /> },
            { path: `/w/:workspaceId/p/:projectId/my-agents/*`, element: <MyAgentsRoutes /> },
            { path: `/w/:workspaceId/p/:projectId/agent-catalogs/*`, element: <AgentCatalogRoutes /> },
            { path: `/w/:workspaceId/p/:projectId/prompts/*`, element: <PromptsRoutes /> },
            { path: `/w/:workspaceId/p/:projectId/marketplace/*`, element: <MarketplaceRoutes /> },

            // Logs
            { path: `/w/:workspaceId/p/:projectId/logs/*`, element: <LogsRoutes /> },
            { path: `/logs/*`, element: <LogsRoutes /> },

            // Legacy routes (keep for backward compatibility)
            { path: `/catalogs/*`, element: <CatalogRoutes /> },
            { path: '/teams/*', element: <TeamRoutes /> },
            { path: `/catalog-view/*`, element: <CatalogViewRoutes /> },
            { path: `/my-page/*`, element: <MyPageRoutes /> },
            { path: `/access-keys/*`, element: <AccessKeysRoutes /> },
            { path: `/openapi-keys/*`, element: <OpenApiKeysRoutes /> },
            { path: `/compare-chats/*`, element: <CompareChatsRoutes /> },
            { path: `/my-agents/*`, element: <MyAgentsRoutes /> },
            { path: `/agent-catalogs/*`, element: <AgentCatalogRoutes /> },
            { path: `/prompts/*`, element: <PromptsRoutes /> },
            { path: `/marketplace/*`, element: <MarketplaceRoutes /> },

            { path: '*', element: <Navigate to="/w" replace /> },
        ],
    },
    { path: `/styling/*`, element: <StyleRoutes /> },
    { path: `/auth/*`, element: <AuthRoutes /> },
    { path: `/policy/*`, element: <PolicyRoutes /> },
    { path: `/public-catalog/*`, element: <CatalogViewRoutes /> },

    { path: '*', element: <Navigate to="/w" replace /> },
]);

export const Router = () => {
    return <RouterProvider router={router} />;
};
